import { debounce } from './utils';
export class QuantitySelector {
  private minusButtons: NodeListOf<HTMLButtonElement>;
  private plusButtons: NodeListOf<HTMLButtonElement>;
  private input: HTMLInputElement | null;
  private container: HTMLElement;
  private lastDispatchedQuantity: number | null = null;
  private allowBeyondMax = false;
  private hasExceededMax = false;
  private isDisabled = false;

  constructor(container: HTMLElement) {
    this.container = container;
    this.minusButtons = container.querySelectorAll('.quantity__btn--minus');
    this.plusButtons = container.querySelectorAll('.quantity__btn--plus');
    this.input = container.querySelector('.quantity__input');

    this.allowBeyondMax = container.getAttribute('data-allow-beyond-max') ? true : false;

    this.isDisabled =
      container.hasAttribute('data-disabled') ||
      this.input?.disabled === true;

    if (this.isDisabled) {
      this.disableUI();
      return;
    }

    this.attachEvents();
    this.updateButtonStates();
  }

  private attachEvents(): void {
    if (!this.input) {
      return;
    }

    this.minusButtons.forEach((btn) =>
      btn.addEventListener('click', this.handleMinusClick.bind(this))
    );

    this.plusButtons.forEach((btn) =>
      btn.addEventListener('click', this.handlePlusClick.bind(this))
    );

    this.input.addEventListener(
      'input',
      debounce(() => this.handleInputChange(), 400)
    );

    this.input.addEventListener('change', this.handleInputSanitize.bind(this));
    this.input.addEventListener('blur', this.handleInputSanitize.bind(this));

    this.input.addEventListener('wheel', (e) => e.preventDefault());
  }

  private handleMinusClick(e: Event): void {
    e.preventDefault();

    if (this.isDisabled || !this.input) {
      return;
    }

    const value = parseInt(this.input.value || '0', 10);
    const min = parseInt(this.input.min || '1', 10);

    if (value > min) {
      this.setValue(value - 1);
    }
  }

  private handlePlusClick(e: Event): void {
    e.preventDefault();
    if (!this.input || this.isDisabled) {
      return;
    }

    const value = parseInt(this.input.value || '0', 10);
    const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;

    // When allowed to exceed max
    if (this.allowBeyondMax) {
      const newValue = value + 1;

      // Detect transition: <= max → > max
      if (value <= max && newValue > max) {
        this.hasExceededMax = true;
        this.updateErrorState(true);
        this.dispatchMaxExceeded(newValue);
      }

      // Update value WITHOUT clamping
      this.setValue(newValue, { bypassMax: true });
      return;
    }

    // Normal behavior (no exceeding)
    if (value < max) {
      this.setValue(value + 1);
    }
  }

  private handleInputChange(): void {
    if (!this.input || this.isDisabled) {
      return;
    }

    // Filter to digits only
    const filtered = this.input.value.replace(/\D/g, '');
    if (filtered !== this.input.value) {
      this.input.value = filtered;
    }

    const value = parseInt(filtered, 10);
    if (isNaN(value)) {
      return;
    }

    const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;

    // Detect exceed when typing
    if (this.allowBeyondMax) {
      if (value > max) {
        if (!this.hasExceededMax) {
          this.hasExceededMax = true;
          this.updateErrorState(true);
          this.dispatchMaxExceeded(value);
        }
      } else {
        if (this.hasExceededMax) {
          this.hasExceededMax = false;
          this.updateErrorState(false);
        }
      }
    }

    // Dispatch quantityChange
    if (value !== this.lastDispatchedQuantity) {
      this.lastDispatchedQuantity = value;
      this.dispatchQuantityChange(value);
    }
  }

  private handleInputSanitize(): void {
    if (!this.input || this.isDisabled) {
      return;
    }

    const min = parseInt(this.input.min || '1', 10);
    const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
    let value = parseInt(this.input.value || '0', 10);

    value = isNaN(value) ? min : value;
    value = Math.max(min, Math.min(value, max));

    this.setValue(value);
  }

  private setValue(
    value: number,
    options: { silent?: boolean; bypassMax?: boolean } = {}
  ): void {
    if (!this.input || this.isDisabled) {
      return;
    }

    const min = parseInt(this.input.min || '1', 10);
    const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;

    // Reset "exceeded" state when value returns below max
    if (value <= max && this.hasExceededMax) {
      this.hasExceededMax = false;
      this.updateErrorState(false);
    }

    // Only clamp max when not bypassing
    if (!options.bypassMax) {
      value = Math.max(min, Math.min(value, max));
    } else {
      value = Math.max(min, value);
    }

    if (this.input.value === value.toString()) {
      this.updateButtonStates();
      return;
    }

    this.input.value = value.toString();
    this.updateButtonStates();

    if (!options.silent) {
      this.lastDispatchedQuantity = value;
      this.dispatchQuantityChange(value);
    }
  }

  public updateButtonStates(): void {
    if (!this.input || this.isDisabled) {
      return;
    }

    const value = parseInt(this.input.value || '0', 10);
    const min = parseInt(this.input.min || '1', 10);
    const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;

    this.minusButtons.forEach((btn) => {
      btn.disabled = value <= min;
    });

    this.plusButtons.forEach((btn) => {
      // If allowed beyond max, never disable
      btn.disabled = !this.allowBeyondMax && value >= max;
    });
  }

  private dispatchQuantityChange(quantity: number): void {
    const event = new CustomEvent('quantityChange', {
      detail: { quantity, input: this.input },
    });
    this.container.dispatchEvent(event);
  }

  public updateMax(newMax: number): void {
    if (this.input) {
      this.input.max = newMax.toString();
      this.setValue(1); // reset to 1 when max updates
    }
  }

  public updateMin(newMin: number): void {
    if (this.input) {
      this.input.min = newMin.toString();
      this.updateButtonStates();
    }
  }

  public disable(): void {
    this.isDisabled = true;
    this.disableUI();
  }

  public enable(): void {
    this.isDisabled = false;
    this.attachEvents();
    this.updateButtonStates();
  }

  private dispatchMaxExceeded(quantity: number): void {
    const evt = new CustomEvent('quantityMaxExceeded', {
      detail: { quantity, input: this.input },
    });
    this.container.dispatchEvent(evt);
  }

  private updateErrorState(exceeded: boolean): void {
    this.container.classList.toggle('quantity--error', exceeded);
  }

  private disableUI(): void {
    this.input?.setAttribute('disabled', 'true');

    this.minusButtons.forEach((btn) => {
      btn.setAttribute('disabled', 'true');
    });

    this.plusButtons.forEach((btn) => {
      btn.setAttribute('disabled', 'true');
    });

    this.container.classList.add('quantity--disabled');
  }
}
